import React, { createContext, useEffect, useState } from 'react';
import useFlowsBillable from 'hooks/useFlowsBillable';
import { FLOW_DRAWER_TYPE, FLOW_VIEW_URL } from 'pages/Flow/constants';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import { canSeeBillableFeatures } from 'utils/permissionUtils';
import { cleanRoom } from 'utils/spaUrls';
import { crumbs } from 'components/Common/BreadcrumbsNavigation';
import { hasNodeListBeenModified } from 'pages/Flow/flowUtils';
import { mutateNodeForAPI } from 'redux/reducer/FlowReducer/utils';
import {
  openSidePanel,
  setFlowDetails,
  updateSelectedFlow,
  updateSelectedFlowInformation,
} from 'redux/actions/FlowAction';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useFetchCleanRoom } from 'hooks/cleanRoomAPIs';
import {
  useGetCleanRoomFlowRunsCount,
  useSetCleanroomFlowVersion,
  useUpdateFlowWithVersion,
  useUpdateNode,
} from 'api/flows';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useUpdateFlow } from 'api/flows/useUpdateFlow';


const getInitialFlowVersioningState = () => ({
  open: false,
  actions: [],
  skipConfirmation: false,
});

const getInitialSaveAsNewVersionState = () => ({
  open: false,
});
const getInitialCloneFlowState = () => ({
  open: false,
});

export const FlowBuilderContext = createContext({});

export const FlowBuilderContextProvider = ({ children }) => {
  const { crFlowId, flowId, crId } = useParams();
  const { pathname, search } = useLocation();
  const history = useHistory();
  const store = useStore();
  const dispatch = useDispatch();
  const flow = useSelector(state => state.flow.flow);
  const cleanRoomFlow = useSelector(state => state.flow.cleanRoomFlow);
  const { ID: orgId } = useSelector(activeOrgSelector);
  const currentFlowNodes = useSelector((state) => state.flow.nodes);
  const [flowVersioningDialog, setFlowVersioningDialog] = useState(
    () => getInitialFlowVersioningState(),
  );
  const [saveAsNewVersionDialog, setSaveAsNewVersionDialog] = useState(
    () => getInitialSaveAsNewVersionState(),
  );
  const [cloneFlowDialog, setCloneFlowDialog] = useState(
    () => getInitialCloneFlowState(),
  );

  const isViewMode = pathname.includes('/view/');
  const isDisabled = pathname.includes(FLOW_VIEW_URL);
  const editMode = !isViewMode && !!crFlowId;
  const breadCrumbs = crId ? [crumbs.crFlow(crId)] : [crumbs.flow];
  const goBackUrl = cleanRoom.flowLanding(crId);

  const { data: crfrsCount } = useGetCleanRoomFlowRunsCount({ crFlowId, crId });
  const { mutate: updateFlow, isSuccess: flowUpdateSuccess } = useUpdateFlow(flowId);
  const { mutate: updateNode } = useUpdateNode(flowId);
  const updateFlowVersion = useSetCleanroomFlowVersion();
  const updateFlowAndCreateNewVersion = useUpdateFlowWithVersion(flowId);

  const {
    data: cleanRoomDetails,
  } = useFetchCleanRoom(orgId, crId, editMode && !!crId);

  // Check if user can see billable toggle (includes user permission and clean room type check)
  const canSeeBillableToggle = canSeeBillableFeatures(cleanRoomDetails?.cleanRoom?.type);

  const {
    isBillable,
    setIsBillable,
    saveBillable,
  } = useFlowsBillable({
    crFlowId,
    enabled: editMode && !!crFlowId && canSeeBillableToggle,
    cleanRoomId: crId,
  });

  const queryParams = React.useMemo(() => new URLSearchParams(search), [search]);
  const version = React.useMemo(() => (isViewMode ? queryParams.get('version') : null), [isViewMode, queryParams]);

  useEffect(() => {
    dispatch(updateSelectedFlow({ flowID: flowId ?? '' }));
  }, [dispatch, flowId]);

  useEffect(() => {
    dispatch(setFlowDetails({
      crFlowId, crId, orgId, flowVersion: version,
    }));
  }, [dispatch, crId, orgId, crFlowId, version, isViewMode]);

  // On Use Version submit, take the user out of the builder
  useEffect(() => {
    if (updateFlowVersion.isSuccess) {
      updateFlowVersion.reset();
      history.push(goBackUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateFlowVersion, history]);

  // On Save as New Version submit, take the user out of the builder
  useEffect(() => {
    if (updateFlowAndCreateNewVersion.isSuccess) {
      updateFlowAndCreateNewVersion.reset();
      history.push(goBackUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateFlowAndCreateNewVersion, history]);

  const handleFlowSubmit = (e) => {
    e.preventDefault();

    const modifiedNodeList = hasNodeListBeenModified(flow.nodes, currentFlowNodes);

    updateFlow({
      cleanRoomID: crId,
      flow,
    });

    if (editMode) {
      if (modifiedNodeList.length > 0) {
        modifiedNodeList.forEach((node) => {
          updateNode({ payload: mutateNodeForAPI(node, flowId), nodeID: node.id });
        });
      }

      if (saveBillable) {
        saveBillable();
      }
    }
  };

  const handleUseFlowVersion = (versionToUse) => {
    updateFlowVersion.mutate({
      cleanRoomId: crId,
      crFlowId,
      version: versionToUse,
    });
  };

  const handleUpdateFlowAndCreateNewVersion = (description = '') => {
    // Get current state directly from the store when function is called
    // (synchronous snapshot of state for imperative logic)
    const currentState = store.getState();
    const latestFlow = currentState.flow.flow;
    const latestNodes = currentState.flow.nodes;
    const latestEdges = currentState.flow.edges;

    updateFlowAndCreateNewVersion.mutate({
      cleanRoomID: crId,
      flow: latestFlow,
      nodes: latestNodes.map((node) => node.id),
      edges: latestEdges.map((edge) => edge.id),
      description,
    });
  };

  const handleAPIGeneration = () => {
    //   logic for generating API
  };

  const handleNameInput = (name) => {
    dispatch(updateSelectedFlowInformation({ type: 'name', value: name }));
  };

  const handleDescriptionInput = (description) => {
    dispatch(updateSelectedFlowInformation({ type: 'description', value: description }));
  };

  const handleParametersDrawerOpen = () => {
    dispatch(openSidePanel({ type: FLOW_DRAWER_TYPE.PARAMETERS, data: {} }));
  };

  const handleSkipConfirmationChange = (shouldSkipConfirmation) => {
    setFlowVersioningDialog(prevState => ({
      ...prevState,
      skipConfirmation: shouldSkipConfirmation,
    }));
  };

  const handleFlowVersioningDialogClose = () => {
    setFlowVersioningDialog(prevState => ({
      ...getInitialFlowVersioningState(),
      skipConfirmation: prevState.skipConfirmation,
    }));
  };

  const handleFlowChangeConfirmation = ({ type, handle }) => {
    if (flowVersioningDialog.skipConfirmation) {
      // Let the user do the action
      handle({ type: 'continue' });

      // exit function to not display dialog
      return;
    }

    // If flow has runs, prompt confirmation to user
    if (crfrsCount !== undefined && crfrsCount > 0) {
      setFlowVersioningDialog(prevState => ({
        open: true,
        // Gather all actions needed to be triggered, fired by react-flow event triggers
        actions: [...prevState.actions, {
          type,
          handle,
        }],
      }));
    }
    else {
      // Otherwise, let the user do the action
      handle({ type: 'continue' });
    }
  };

  const handleSaveAsNewVersionDialogOpen = () => {
    setSaveAsNewVersionDialog(prevState => ({
      ...prevState,
      open: true,
    }));
  };

  const handleSaveAsNewVersionDialogClose = () => {
    setSaveAsNewVersionDialog(({
      ...getInitialSaveAsNewVersionState(),
    }));
  };

  const handleCloneFlowDialogOpen = () => {
    setCloneFlowDialog(prevState => ({
      ...prevState,
      open: true,
    }));
  };

  const handleCloneFlowDialogClose = () => {
    setCloneFlowDialog(({
      ...getInitialCloneFlowState(),
    }));
  };

  const flowBuilderContextValue = {
    flowId,
    crFlowId,
    crId,
    flow,
    cleanRoomFlow,
    editMode,
    breadCrumbs,
    isDisabled,
    isViewMode,
    flowUpdateSuccess,
    flowVersioningDialog,
    saveAsNewVersionDialog,
    cloneFlowDialog,
    saveAsNewVersionLoading: updateFlowAndCreateNewVersion.isLoading,

    // Billable flow state
    isBillable,
    setIsBillable,
    canSeeBillableToggle,

    handleFlowSubmit,
    handleUseFlowVersion,
    handleUpdateFlowAndCreateNewVersion,
    handleAPIGeneration,
    handleNameInput,
    handleDescriptionInput,
    handParametersDrawerOpen: handleParametersDrawerOpen,
    handleFlowVersioningDialogClose,
    handleFlowChangeConfirmation,
    handleSkipConfirmationChange,
    handleSaveAsNewVersionDialogOpen,
    handleSaveAsNewVersionDialogClose,
    handleCloneFlowDialogOpen,
    handleCloneFlowDialogClose,
    goBackUrl,
  };

  return (
    <FlowBuilderContext.Provider value={flowBuilderContextValue}>
      {children}
    </FlowBuilderContext.Provider>
  );
};
